/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  Method,
} from "axios";
import {
  AppConfig,
  DialogueMessage,
  GaimaTask,
  Goal,
  Intro,
  KnowledgeComponent,
  LearnerType,
  Lesson,
  Organization,
  Plan,
  Survey,
  Topic,
  User,
  UserAccessToken,
  UserRole,
} from "./types";
import {
  APP_CONFIG_QUERY,
  GOAL_QUERY,
  TOPIC_QUERY,
  LESSON_QUERY,
  KC_QUERY,
  LEARNER_TYPE_QUERY,
  DIALOGUE_MESSAGE_QUERY,
  INTRO_QUERY,
  PLAN_QUERY,
  SURVEY_QUERY,
  USER_QUERY,
  ORG_QUERY,
} from "./queries";

const GRAPHQL_ENDPOINT = process.env.GRAPHQL_ENDPOINT || "/graphql";
const GAIMA_ENDPOINT = process.env.GAIMA_ENDPOINT || "/gaima";

interface Connection<T> {
  edges: {
    node: T;
    cursor: string;
  }[];
  pageInfo: {
    startCursor: string;
    endCursor: string;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
}

interface GQLRequest {
  uri?: string;
  query: string;
  variables?: Record<string, unknown>;
}

interface GQLResponse<T> {
  errors?: { message: string }[];
  data?: T;
}

// https://github.com/axios/axios/issues/4193#issuecomment-1158137489
interface MyAxiosRequestConfig extends Omit<AxiosRequestConfig, "headers"> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headers?: any; // this was "any" at v0.21.1 but now broken between 0.21.4 >= 0.27.2
}

/**
 * Middleware function takes some action on an axios instance
 */
interface AxiosMiddleware {
  (axiosInstance: AxiosInstance): void;
}

interface HttpRequestConfig {
  accessToken?: string; // bearer-token http auth
  axiosConfig?: MyAxiosRequestConfig; // any axios config for the request
  axiosMiddleware?: AxiosMiddleware; // used (for example) to extract accessToken from response headers
  /**
   * When set, will use this prop (or array of props) to extract return data from a json response, e.g.
   *
   * dataPath: ["foo", "bar"]
   *
   * // will extract "barvalue" for the return
   * { "foo": { "bar": "barvalue" } }
   */
  dataPath?: string | string[];
}

function getDataFromAxiosResponse(res: AxiosResponse, path: string | string[]) {
  throwErrorsInAxiosResponse(res);
  let data = res.data.data;
  if (!data) {
    throw new Error(`no data in reponse: ${JSON.stringify(res.data)}`);
  }
  const dataPath = Array.isArray(path)
    ? path
    : typeof path === "string"
      ? [path]
      : [];
  dataPath.forEach((pathPart) => {
    if (!data) {
      throw new Error(
        `unexpected response data shape for dataPath ${JSON.stringify(
          dataPath
        )} and request ${res.request} : ${res.data}`
      );
    }
    data = data[pathPart];
  });
  return data;
}

export async function execHttp<T>(
  method: Method,
  query: string,
  opts?: HttpRequestConfig
): Promise<T> {
  const optsEffective: HttpRequestConfig = opts || {};
  const axiosConfig = opts?.axiosConfig || {};
  const axiosInst = axios.create();
  if (optsEffective.axiosMiddleware) {
    optsEffective.axiosMiddleware(axiosInst);
  }
  const result = await axiosInst.request({
    url: query,
    method: method,
    ...axiosConfig,
    headers: {
      ...(axiosConfig.headers || {}), // if any headers passed in opts, include them
      ...(optsEffective && optsEffective.accessToken // if accessToken passed in opts, add auth to headers
        ? { Authorization: `bearer ${optsEffective.accessToken}` }
        : {}),
    },
  });
  return getDataFromAxiosResponse(result, optsEffective.dataPath || []);
}

export async function fetchGql<T>(
  req: GQLRequest,
  headers?: Record<string, string>
): Promise<T> {
  const result = await axios.post<GQLResponse<T>>(
    req.uri || GRAPHQL_ENDPOINT,
    req,
    {
      headers: headers || {},
    }
  );
  if (!result.data.data) {
    throw new Error(
      Array.isArray(result.data.errors)
        ? JSON.stringify(result.data.errors)
        : `failed to return expected data for query ${JSON.stringify(req)}`
    );
  }
  if (result.data.errors?.length) {
    throw new Error(result.data.errors[0].message);
  }
  return result.data.data;
}

export function throwErrorsInAxiosResponse(res: AxiosResponse): void {
  if (!(res.status >= 200 && res.status <= 299)) {
    throw new Error(`http request failed: ${res.data}`);
  }
  if (res.data.errors) {
    throw new Error(`errors in response: ${JSON.stringify(res.data.errors)}`);
  }
}

/** GAIMA polling */

export async function fetchGaimaResult(
  initialDocument: string,
  learnerCharacteristics: string[],
  modelName?: string,
  maxIterations?: number,
  pipelineType?: string,
  useRealStreaming?: boolean
): Promise<GaimaTask> {
  return execHttp<GaimaTask>("POST", GAIMA_ENDPOINT, {
    axiosConfig: {
      data: {
        initialDocument,
        learnerCharacteristics,
        modelName: modelName,
        maxIterations: maxIterations,
        pipelineType: pipelineType,
        useRealStreaming: useRealStreaming,
      },
    },
  });
}

export async function pollGaima(pollId: string): Promise<GaimaTask> {
  return execHttp<GaimaTask>("GET", `${GAIMA_ENDPOINT}/status/${pollId}`, {});
}

/** queries */

export async function fetchAppConfig(accessToken: string): Promise<AppConfig> {
  const result = await fetchGql<{ me: { appConfig: AppConfig } }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        query AppConfig {
          me {
            appConfig {
              ${APP_CONFIG_QUERY}
            }
          }
        }
    `,
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.appConfig;
}
export async function fetchGoals(accessToken: string): Promise<Goal[]> {
  const result = await fetchGql<{ me: { goals: Connection<Goal> } }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        query Goals {
          me {
            goals {
              edges {
                node {
                  ${GOAL_QUERY}
                }
              }
            }
          }
        }
      `,
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.goals.edges.map((e) => e.node);
}
export async function fetchModules(accessToken: string): Promise<Topic[]> {
  const result = await fetchGql<{ me: { topics: Connection<Topic> } }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        query Topics {
          me {
            topics {
              edges {
                node {
                  ${TOPIC_QUERY}
                }
              }
            }
          }
        }
      `,
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.topics.edges.map((e) => e.node);
}
export async function fetchLessons(accessToken: string): Promise<Lesson[]> {
  const result = await fetchGql<{ me: { lessons: Connection<Lesson> } }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        query Lessons {
          me {
            lessons {
              edges {
                node {
                  ${LESSON_QUERY}
                }
              }
            }
          }
        }
    `,
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.lessons.edges.map((e) => e.node);
}

export async function fetchKCs(): Promise<KnowledgeComponent[]> {
  const result = await fetchGql<{
    knowledgeComponents: Connection<KnowledgeComponent>;
  }>({
    uri: GRAPHQL_ENDPOINT,
    query: `
      query KnowledgeComponents {
        knowledgeComponents {
          edges {
            node {
              ${KC_QUERY}
            }
          }
        }
      }
    `,
  });
  return result.knowledgeComponents.edges.map((e) => e.node);
}
export async function fetchLearnerTypes(): Promise<LearnerType[]> {
  const result = await fetchGql<{ learnerTypes: Connection<LearnerType> }>({
    uri: GRAPHQL_ENDPOINT,
    query: `
      query LearnerTypes {
        learnerTypes {
          edges {
            node {
              ${LEARNER_TYPE_QUERY}
            }
          }
        }
      }
    `,
  });
  return result.learnerTypes.edges.map((e) => e.node);
}
export async function fetchDialogueMessages(): Promise<DialogueMessage[]> {
  const result = await fetchGql<{
    dialogueMessages: Connection<DialogueMessage>;
  }>({
    uri: GRAPHQL_ENDPOINT,
    query: `
      query DialogueMessages {
        dialogueMessages {
          edges {
            node {
              ${DIALOGUE_MESSAGE_QUERY}
            }
          }
        }
      }
    `,
  });
  return result.dialogueMessages.edges.map((e) => e.node);
}

export async function fetchIntros(): Promise<Intro[]> {
  const result = await fetchGql<{ intros: Connection<Intro> }>({
    uri: GRAPHQL_ENDPOINT,
    query: `
      query Intros {
        intros {
          edges {
            node {
              ${INTRO_QUERY}
            }
          }
        }
      }
    `,
  });
  return result.intros.edges.map((e) => e.node);
}
export async function fetchPlans(): Promise<Plan[]> {
  const result = await fetchGql<{ plans: Connection<Plan> }>({
    uri: GRAPHQL_ENDPOINT,
    query: `
      query Plans {
        plans {
          edges {
            node {
              ${PLAN_QUERY}
            }
          }
        }
      }
    `,
  });
  return result.plans.edges.map((e) => e.node);
}
export async function fetchSurveys(): Promise<Survey[]> {
  const result = await fetchGql<{ surveys: Connection<Survey> }>({
    uri: GRAPHQL_ENDPOINT,
    query: `
      query Surveys {
        surveys {
          edges {
            node {
              ${SURVEY_QUERY}
            }
          }
        }
      }
    `,
  });
  return result.surveys.edges.map((e) => e.node);
}

export async function fetchUsers(accessToken: string): Promise<User[]> {
  const result = await fetchGql<{ me: { users: Connection<User> } }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        query Users {
          me {
            users {
              edges {
                node {
                  ${USER_QUERY}
                }
              }
            }
          }
        }
      `,
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.users.edges.map((e) => e.node);
}
export async function fetchOrgs(accessToken: string): Promise<Organization[]> {
  const result = await fetchGql<{
    me: { organizations: Connection<Organization> };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
      query Organizations {
        me {
          organizations {
            edges {
              node {
                ${ORG_QUERY}
              }
            }
          }
        }
      }
    `,
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.organizations.edges.map((e) => e.node);
}

/** mutations */

export async function signupWithPassword(
  name: string,
  password: string,
  email: string
): Promise<UserAccessToken> {
  const result = await fetchGql<{ signup: UserAccessToken }>({
    uri: GRAPHQL_ENDPOINT,
    query: `
      mutation Signup($name: String, $password: String, $email: String) {
        signup(name: $name, password: $password, email: $email) {
          user {
            ${USER_QUERY}
          }
          accessToken
        }
      }
    `,
    variables: { name, password, email },
  });
  return result.signup;
}
export async function loginWithPassword(
  username: string,
  password: string
): Promise<UserAccessToken> {
  const result = await fetchGql<{ loginWithPassword: UserAccessToken }>({
    uri: GRAPHQL_ENDPOINT,
    query: `
      mutation LoginWithPassword($username: String, $password: String) {
        loginWithPassword(username: $username, password: $password) {
          user {
            ${USER_QUERY}
          }
          accessToken
        }
      }
    `,
    variables: { username, password },
  });
  return result.loginWithPassword;
}
export async function loginWithToken(
  accessToken: string
): Promise<UserAccessToken> {
  const result = await fetchGql<{ login: UserAccessToken }>({
    uri: GRAPHQL_ENDPOINT,
    query: `
      mutation LoginWithToken($accessToken: String) {
        login(accessToken: $accessToken) {
          user {
            ${USER_QUERY}
          }
          accessToken
        }
      }
    `,
    variables: { accessToken },
  });
  return result.login;
}

export async function updateAppConfig(
  accessToken: string,
  config: Partial<AppConfig>
): Promise<AppConfig> {
  const result = await fetchGql<{
    me: { admin: { updateAppConfig: AppConfig } };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdateAppConfig($config: UpdateAppConfig!) {
          me {
            admin {
              updateAppConfig(config: $config) {
                ${APP_CONFIG_QUERY}
              }
            }
          }
        }
    `,
      variables: { config },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.admin.updateAppConfig;
}
export async function updateUserPermissions(
  accessToken: string,
  userId: string,
  permissionLevel: UserRole
): Promise<User> {
  const result = await fetchGql<{
    me: { admin: { updateUserPermissions: User } };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdateUserPermissions($userId: String!, $permissionLevel: String!) {
          me {
            admin {
              updateUserPermissions(userId: $userId, permissionLevel: $permissionLevel) {
                ${USER_QUERY}
              }
            }
          }
        }
    `,
      variables: { userId, permissionLevel },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.admin.updateUserPermissions;
}

export async function updateGoal(
  accessToken: string,
  goal: Partial<Goal>
): Promise<Goal> {
  const result = await fetchGql<{
    me: { updateGoal: Goal };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdateGoal($id: ID, $goal: UpdateGoal!) {
          me {
            updateGoal(id: $id, goal: $goal) {
              ${GOAL_QUERY}
            }
          }
        }
    `,
      variables: {
        id: goal.id,
        goal: {
          name: goal.name,
          desc: goal.desc,
          hidden: goal.hidden,
          deleted: goal.deleted,
          topics: goal.topics,
          plans: goal.plans,
          survey: goal.survey,
          org: goal.org,
          apiOverrides: goal.apiOverrides,
          learnerTypes: goal.learnerTypes,
        },
      },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.updateGoal;
}
export async function deleteGoal(
  accessToken: string,
  id: string
): Promise<Goal> {
  const result = await fetchGql<{
    me: { deleteGoal: Goal };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation DeleteGoal($id: ID!) {
          me {
            deleteGoal(id: $id) {
              ${GOAL_QUERY}
            }
          }
        }
    `,
      variables: { id },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.deleteGoal;
}
export async function updateTopic(
  accessToken: string,
  topic: Partial<Topic>
): Promise<Topic> {
  const result = await fetchGql<{
    me: { updateTopic: Topic };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdateTopic($id: ID, $topic: UpdateTopic!) {
          me {
            updateTopic(id: $id, topic: $topic) {
              ${TOPIC_QUERY}
            }
          }
        }
    `,
      variables: {
        id: topic.id,
        topic: {
          name: topic.name,
          desc: topic.desc,
          hidden: topic.hidden,
          org: topic.org,
          recommender: topic.recommender,
          lessons: topic.lessons,
          requiredLessons: topic.requiredLessons,
          prerequisiteTopics: topic.prerequisiteTopics,
          knowledgeComponents: topic.knowledgeComponents,
          attributeRelevance: topic.attributeRelevance,
        },
      },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.updateTopic;
}
export async function deleteTopic(
  accessToken: string,
  id: string
): Promise<Topic> {
  const result = await fetchGql<{
    me: { deleteTopic: Topic };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation DeleteTopic($id: ID!) {
          me {
            deleteTopic(id: $id) {
              ${TOPIC_QUERY}
            }
          }
        }
    `,
      variables: { id },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.deleteTopic;
}
export async function updateLesson(
  accessToken: string,
  lesson: Partial<Lesson>
): Promise<Lesson> {
  const result = await fetchGql<{
    me: { updateLesson: Lesson };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdateLesson($id: ID, $lesson: UpdateLesson!) {
          me {
            updateLesson(id: $id, lesson: $lesson) {
              ${LESSON_QUERY}
            }
          }
        }
    `,
      variables: {
        id: lesson.id,
        lesson: {
          name: lesson.name,
          desc: lesson.desc,
          hidden: lesson.hidden,
          org: lesson.org,
          type: lesson.type,
          downloadable: lesson.downloadable,
          resources: lesson.resources?.map((r) => {
            return {
              uri: r.uri,
              duration: r.duration,
              explorationLevel: r.explorationLevel,
              assets: r.assets,
              knowledgeComponents: r.knowledgeComponents,
              license: r.license,
              firstTimeCategory: r.firstTimeCategory,
              firstTimeParams: r.firstTimeParams,
              inlineMcq: r.inlineMcq,
              inlineOpentutor: r.inlineOpentutor,
            };
          }),
          randomizeResources: lesson.randomizeResources,
          remediationScore: lesson.remediationScore,
          remedialLessons: lesson.remedialLessons,
          remedialRequirements: lesson.remedialRequirements,
          traitRequirements: lesson.traitRequirements,
          learnerTypes: lesson.learnerTypes,
        },
      },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.updateLesson;
}
export async function deleteLesson(
  accessToken: string,
  id: string
): Promise<Lesson> {
  const result = await fetchGql<{
    me: { deleteLesson: Lesson };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation DeleteLesson($id: ID!) {
          me {
            deleteLesson(id: $id) {
              ${LESSON_QUERY}
            }
          }
        }
    `,
      variables: { id },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.deleteLesson;
}

export async function createKnowledgeComponent(
  accessToken: string,
  knowledgeComponent: Partial<KnowledgeComponent>
): Promise<KnowledgeComponent> {
  const result = await fetchGql<{
    me: { createKnowledgeComponent: KnowledgeComponent };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation CreateKnowledgeComponent($knowledgeComponent: UpdateKnowledgeComponent!) {
          me {
            createKnowledgeComponent(knowledgeComponent: $knowledgeComponent) {
              ${KC_QUERY}
            }
          }
        }
    `,
      variables: {
        knowledgeComponent: {
          name: knowledgeComponent.name,
          desc: knowledgeComponent.desc,
        },
      },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.createKnowledgeComponent;
}
export async function deleteKnowledgeComponent(
  accessToken: string,
  id: string
): Promise<KnowledgeComponent> {
  const result = await fetchGql<{
    me: { deleteKnowledgeComponent: KnowledgeComponent };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation DeleteKnowledgeComponent($id: ID!) {
          me {
            deleteKnowledgeComponent(id: $id) {
              ${KC_QUERY}
            }
          }
        }
    `,
      variables: { id },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.deleteKnowledgeComponent;
}
export async function updateLearnerType(
  accessToken: string,
  learnerType: Partial<LearnerType>
): Promise<LearnerType> {
  const result = await fetchGql<{
    me: { updateLearnerType: LearnerType };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdateLearnerType($id: ID, $learnerType: UpdateLearnerType!) {
          me {
            updateLearnerType(id: $id, learnerType: $learnerType) {
              ${LEARNER_TYPE_QUERY}
            }
          }
        }
    `,
      variables: {
        id: learnerType.id,
        learnerType: {
          name: learnerType.name,
          desc: learnerType.desc,
          characteristics: learnerType.characteristics,
        },
      },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.updateLearnerType;
}
export async function deleteLearnerType(
  accessToken: string,
  id: string
): Promise<LearnerType> {
  const result = await fetchGql<{
    me: { deleteLearnerType: LearnerType };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation DeleteLearnerType($id: ID!) {
          me {
            deleteLearnerType(id: $id) {
              ${LEARNER_TYPE_QUERY}
            }
          }
        }
    `,
      variables: { id },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.deleteLearnerType;
}
export async function updateDialogueMessage(
  accessToken: string,
  dialogueMessage: Partial<DialogueMessage>
): Promise<DialogueMessage> {
  const result = await fetchGql<{
    me: { updateDialogueMessage: DialogueMessage };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdateDialogueMessage($id: ID, $dialogueMessage: UpdateDialogueMessage!) {
          me {
            updateDialogueMessage(id: $id, dialogueMessage: $dialogueMessage) {
              ${DIALOGUE_MESSAGE_QUERY}
            }
          }
        }
    `,
      variables: {
        id: dialogueMessage.id,
        dialogueMessage: {
          text: dialogueMessage.text,
          type: dialogueMessage.type,
          animation: dialogueMessage.animation,
          audioText: dialogueMessage.audioText,
          audioFile: dialogueMessage.audioFile,
        },
      },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.updateDialogueMessage;
}
export async function deleteDialogueMessage(
  accessToken: string,
  id: string
): Promise<DialogueMessage> {
  const result = await fetchGql<{
    me: { deleteDialogueMessage: DialogueMessage };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation DeleteDialogueMessage($id: ID!) {
          me {
            deleteDialogueMessage(id: $id) {
              ${DIALOGUE_MESSAGE_QUERY}
            }
          }
        }
    `,
      variables: { id },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.deleteDialogueMessage;
}

export async function updateIntro(
  accessToken: string,
  intro: Partial<Intro>
): Promise<Intro> {
  const result = await fetchGql<{
    me: { updateIntro: Intro };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdateIntro($id: ID, $intro: UpdateIntro!) {
          me {
            updateIntro(id: $id, intro: $intro) {
              ${INTRO_QUERY}
            }
          }
        }
    `,
      variables: { id: intro.id, intro },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.updateIntro;
}
export async function deleteIntro(
  accessToken: string,
  id: string
): Promise<Intro> {
  const result = await fetchGql<{
    me: { deleteIntro: Intro };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation DeleteIntro($id: ID!) {
          me {
            deleteIntro(id: $id) {
              ${INTRO_QUERY}
            }
          }
        }
    `,
      variables: { id },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.deleteIntro;
}
export async function updatePlan(
  accessToken: string,
  plan: Partial<Plan>
): Promise<Plan> {
  const result = await fetchGql<{
    me: { updatePlan: Plan };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdatePlan($id: ID, $plan: UpdatePlan!) {
          me {
            updatePlan(id: $id, plan: $plan) {
              ${PLAN_QUERY}
            }
          }
        }
    `,
      variables: { id: plan.id, plan },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.updatePlan;
}
export async function deletePlan(
  accessToken: string,
  id: string
): Promise<Plan> {
  const result = await fetchGql<{
    me: { deletePlan: Plan };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation DeletePlan($id: ID!) {
          me {
            deletePlan(id: $id) {
              ${PLAN_QUERY}
            }
          }
        }
    `,
      variables: { id },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.deletePlan;
}
export async function updateSurvey(
  accessToken: string,
  survey: Partial<Survey>
): Promise<Survey> {
  const result = await fetchGql<{
    me: { updateSurvey: Survey };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdateSurvey($id: ID, $survey: UpdateSurvey!) {
          me {
            updateSurvey(id: $id, survey: $survey) {
              ${SURVEY_QUERY}
            }
          }
        }
    `,
      variables: { id: survey.id, survey },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.updateSurvey;
}
export async function deleteSurvey(
  accessToken: string,
  id: string
): Promise<Survey> {
  const result = await fetchGql<{
    me: { deleteSurvey: Survey };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation DeleteSurvey($id: ID!) {
          me {
            deleteSurvey(id: $id) {
              ${SURVEY_QUERY}
            }
          }
        }
    `,
      variables: { id },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.deleteSurvey;
}

export async function updateOrganization(
  accessToken: string,
  organization: Partial<Organization>
): Promise<Organization> {
  const result = await fetchGql<{
    me: { updateOrganization: Organization };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation UpdateOrganization($id: ID, $organization: UpdateOrganization!) {
          me {
            updateOrganization(id: $id, organization: $organization) {
              ${ORG_QUERY}
            }
          }
        }
    `,
      variables: {
        id: organization.id,
        organization: {
          name: organization.name,
          desc: organization.desc,
          hidden: organization.hidden,
          accessCode: organization.accessCode,
          members: organization.members,
          config: organization.config,
        },
      },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.updateOrganization;
}
export async function deleteOrganization(
  accessToken: string,
  id: string
): Promise<Organization> {
  const result = await fetchGql<{
    me: { deleteOrganization: Organization };
  }>(
    {
      uri: GRAPHQL_ENDPOINT,
      query: `
        mutation DeleteOrganization($id: ID!) {
          me {
            deleteOrganization(id: $id) {
              ${ORG_QUERY}
            }
          }
        }
    `,
      variables: { id },
    },
    {
      Authorization: `bearer ${accessToken}`,
    }
  );
  return result.me.deleteOrganization;
}
